import styled from "styled-components";
import { useState } from "react";

/* ================= PAGE ================= */

const Page = styled.section`
  padding: 120px 20px 80px;
  background: ${({ theme }) => theme.colors.bg};
`;

const Container = styled.div`
  max-width: 1100px;
  margin: auto;
`;

const Heading = styled.h1`
  text-align: center;
  font-size: clamp(28px, 6vw, 54px);
  font-weight: 900;
`;

const Sub = styled.p`
  max-width: 820px;
  margin: 20px auto 60px;
  text-align: center;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= CTA ================= */

const CTAWrapper = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #4ade80, #22c55e);
  color: #020617;
  font-weight: 800;
  padding: 16px 28px;
  border-radius: 14px;
  border: none;
  font-size: 16px;
  cursor: pointer;
`;

const CTASub = styled.p`
  margin-top: 10px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ================= MODAL ================= */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #020617;
  border-radius: 20px;
  padding: 30px;
  width: 100%;
  max-width: 480px;
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 20px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  label {
    font-size: 13px;
    margin-bottom: 6px;
    color: #94a3b8;
  }

  textarea,
  select,
  input {
    padding: 10px;
    border-radius: 10px;
    background: #020617;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }

  textarea {
    resize: vertical;
  }
`;

const Button = styled.button`
  width: 100%;
  margin-top: 10px;
  padding: 14px;
  background: #4ade80;
  color: #020617;
  font-weight: 800;
  border: none;
  border-radius: 12px;
  cursor: pointer;
`;

const ResultBox = styled.div`
  margin-top: 20px;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 14px;

  h3 {
    margin-bottom: 6px;
  }

  ul {
    margin-top: 8px;
    padding-left: 18px;
    font-size: 14px;
  }
`;

/* ================= COMPONENT ================= */

export default function ATSResumeGuidePage() {
  const [show, setShow] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [role, setRole] = useState("");
  const [experience, setExperience] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  /* ================= UPLOAD HANDLER ================= */

  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // TXT → auto read
    if (file.type === "text/plain") {
      const reader = new FileReader();
      reader.onload = () => setResumeText(reader.result);
      reader.readAsText(file);
      return;
    }

    // PDF → honest limitation
    if (file.type === "application/pdf") {
      alert(
        "PDF parsing in browser is unreliable.\n\nPlease copy-paste your resume text below for accurate ATS analysis."
      );
    }
  };

  /* ================= ANALYSIS ================= */

  const analyseResume = async () => {
    if (!resumeText || !role || !experience) {
      alert("Please upload or paste resume text and select role & experience");
      return;
    }

    try {
      setLoading(true);
      setResult(null);

      const res = await fetch("https://api.openai.com/v1/chat/completions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",
          messages: [
            {
              role: "system",
              content: `
You are an ATS (Applicant Tracking System) used by recruiters.

Rules:
- Score from 0–100
- Strict & realistic
- Role + experience specific
- Output ONLY valid JSON
`,
            },
            {
              role: "user",
              content: `
Target Role: ${role}
Experience Level: ${experience}

Resume Text:
"""
${resumeText}
"""

Return JSON:
{
  "score": number,
  "verdict": "Good fit | Needs improvement | Not suitable",
  "strengths": ["..."],
  "improvements": ["..."]
}
`,
            },
          ],
          temperature: 0.2,
        }),
      });

      const data = await res.json();
      const parsed = JSON.parse(data.choices[0].message.content);

      setResult({
        score: parsed.score,
        verdict:
          parsed.score >= 75
            ? "✅ Good fit for this role"
            : parsed.score >= 50
            ? "⚠️ Needs improvement"
            : "❌ Not suitable",
        strengths: parsed.strengths,
        tips: parsed.improvements,
      });
    } catch (e) {
      alert("Analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Container>
        <Heading>ATS Resume Score Checker</Heading>
        <Sub>
          Check if your resume will pass ATS for a specific role — before
          recruiters reject it.
        </Sub>

        <CTAWrapper>
          <CTAButton onClick={() => setShow(true)}>
            Check ATS Score (Free)
          </CTAButton>
          <CTASub>
            Upload resume or paste text • Select role • Get ATS score
          </CTASub>
        </CTAWrapper>
      </Container>

      {show && (
        <Overlay onClick={() => setShow(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <Title>ATS Resume Analysis</Title>

            <Field>
              <label>Upload Resume (TXT / PDF)</label>
              <input
                type="file"
                accept=".txt,.pdf"
                onChange={handleResumeUpload}
              />
            </Field>

            <Field>
              <label>Paste Resume Text (Recommended)</label>
              <textarea
                rows={6}
                placeholder="Paste your resume text here for best ATS accuracy"
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
              />
            </Field>

            <Field>
              <label>Target Role</label>
              <select onChange={(e) => setRole(e.target.value)}>
                <option value="">Select role</option>
                <option>Frontend Developer</option>
                <option>Backend Developer</option>
                <option>React Developer</option>
                <option>React Native Developer</option>
              </select>
            </Field>

            <Field>
              <label>Experience Level</label>
              <select onChange={(e) => setExperience(e.target.value)}>
                <option value="">Select experience</option>
                <option>Fresher (0–1)</option>
                <option>Mid (1–3)</option>
                <option>Senior (3+)</option>
              </select>
            </Field>

            <Button onClick={analyseResume} disabled={loading}>
              {loading ? "Analysing..." : "Analyse Resume"}
            </Button>

            {result && (
              <ResultBox>
                <h3>ATS Score: {result.score}/100</h3>
                <p>{result.verdict}</p>

                <strong>Improvements:</strong>
                <ul>
                  {result.tips.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </ResultBox>
            )}
          </Modal>
        </Overlay>
      )}
    </Page>
  );
}
